import type {
  AppLocale,
  LeaderboardResponse,
  LeaderboardRow,
} from '@ielts-buddy/shared'
import { useCallback, useEffect, useState } from 'react'
import { t } from '../i18n/messages.js'
import { ApiError, fetchLeaderboard } from '../shared/api.js'
import { useAuthStore } from '../shared/auth-store.js'
import { usePreferencesStore } from '../shared/preferences-store.js'
import { useRouter } from '../shared/router-store.js'
import { haptics } from '../shared/telegram.js'
import { TrophyIcon } from '../shared/ui/icons.js'
import {
  Avatar,
  Button,
  Card,
  ErrorState,
  LoadingDots,
} from '../shared/ui/index.js'

function initialsFromDisplayName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) {
    const w = parts[0]
    return w ? w.slice(0, 2).toUpperCase() : '?'
  }
  const a = parts[0]?.[0]
  const b = parts[parts.length - 1]?.[0]
  return a && b ? `${a}${b}`.toUpperCase() : '?'
}

type PodiumPlace = 1 | 2 | 3

/** Rank digit on the pedestal: 1st (centre) largest, then 2nd, 3rd. */
function podiumPedestalRankClass(place: PodiumPlace): string {
  if (place === 1)
    return 'text-[2.35rem] sm:text-[2.55rem] font-bold tabular-nums text-brand-text-subtle/50'
  if (place === 2)
    return 'text-[1.85rem] sm:text-[2rem] font-bold tabular-nums text-brand-text-subtle/45'
  return 'text-[1.5rem] sm:text-[1.7rem] font-bold tabular-nums text-brand-text-subtle/40'
}

function podiumPedestalHeight(place: PodiumPlace): string {
  if (place === 1) return 'h-[5.5rem]'
  if (place === 2) return 'h-[4.25rem]'
  return 'h-[3.25rem]'
}

export function LeaderboardScreen() {
  const locale = usePreferencesStore((s) => s.locale)
  const navigateHome = useRouter((s) => s.navigateHome)
  const userId = useAuthStore((s) => s.user?.id)
  const [data, setData] = useState<LeaderboardResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const res = await fetchLeaderboard()
      setData(res)
    } catch (e) {
      setData(null)
      const raw = e instanceof ApiError ? e.message : 'Failed to load'
      const msg =
        e instanceof ApiError && raw.includes('Invalid leaderboard')
          ? t(locale, 'leaderboard.errorInvalid')
          : raw
      setError(msg)
    }
  }, [locale])

  useEffect(() => {
    void load()
  }, [load])

  if (error) {
    return (
      <div className="tab-screen screen-padding flex flex-col gap-4 animate-fade-in">
        <div>
          <h1 className="text-xl font-semibold text-brand-text">
            {t(locale, 'leaderboard.title')}
          </h1>
          <p className="text-sm text-brand-text-muted mt-1">
            {t(locale, 'leaderboard.subtitle')}
          </p>
        </div>
        <ErrorState
          title={t(locale, 'leaderboard.loadError')}
          message={error}
          primaryAction={{
            label: t(locale, 'leaderboard.retry'),
            onClick: () => void load(),
          }}
        />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="tab-screen screen-padding flex flex-col gap-4">
        <h1 className="text-xl font-semibold text-brand-text">
          {t(locale, 'leaderboard.title')}
        </h1>
        <LoadingDots label={t(locale, 'common.loading')} />
      </div>
    )
  }

  const rows = data.rows ?? []
  if (rows.length === 0) {
    return (
      <div className="tab-screen screen-padding flex flex-col gap-3 animate-fade-in">
        <h1 className="shrink-0 text-xl font-semibold text-brand-text">
          {t(locale, 'leaderboard.title')}
        </h1>
        <div className="flex min-h-0 flex-1 flex-col justify-center py-1">
          <Card
            size="default"
            tone="surface"
            className="mx-auto flex min-h-0 w-full max-w-sm flex-col items-center gap-3 overflow-y-auto border border-brand-settings-outline p-4 text-center sm:p-5"
          >
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-primary-soft text-brand-primary-strong"
              aria-hidden
            >
              <TrophyIcon className="h-6 w-6" />
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <h2 className="text-base font-semibold leading-snug text-brand-text">
                {t(locale, 'leaderboard.emptyTitle')}
              </h2>
              <p className="text-sm leading-snug text-brand-text-muted">
                {t(locale, 'leaderboard.emptyBody')}
              </p>
            </div>
            <Button
              variant="gradient"
              type="button"
              className="w-full shrink-0"
              onClick={() => {
                haptics.medium()
                navigateHome()
              }}
            >
              {t(locale, 'results.goHome')}
            </Button>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="tab-screen screen-padding flex flex-col gap-4 animate-fade-in pb-2">
      <div>
        <h1 className="text-xl font-semibold text-brand-text">
          {t(locale, 'leaderboard.title')}
        </h1>
        <p className="text-sm text-brand-text-muted mt-1">
          {t(locale, 'leaderboard.subtitle')}
        </p>
      </div>

      {data.me?.avgBand != null && (
        <Card size="flat" tone="primary-soft" className="p-4">
          <div className="text-[11px] uppercase tracking-wider text-brand-primary font-semibold mb-1">
            {t(locale, 'leaderboard.you')}
          </div>
          <div className="flex justify-between text-sm text-brand-text">
            <span>
              {data.me?.rank != null
                ? `${t(locale, 'leaderboard.rank')} ${data.me.rank}`
                : '—'}
            </span>
            <span className="font-semibold text-brand-primary-strong">
              {data.me?.avgBand} · {data.me?.sessionsScored ?? 0}{' '}
              {t(locale, 'leaderboard.sessions')}
            </span>
          </div>
        </Card>
      )}

      <LeaderboardPodiumAndList locale={locale} rows={rows} userId={userId} />
    </div>
  )
}

function LeaderboardPodiumAndList({
  locale,
  rows,
  userId,
}: {
  locale: AppLocale
  rows: LeaderboardRow[]
  userId: string | undefined
}) {
  const top3 = rows.slice(0, 3)
  const rest = rows.slice(3)

  const slots: { place: PodiumPlace; row: LeaderboardRow | null }[] =
    top3.length === 0
      ? [
          { place: 2, row: null },
          { place: 1, row: null },
          { place: 3, row: null },
        ]
      : top3.length === 1
        ? [
            { place: 2, row: null },
            { place: 1, row: top3[0] ?? null },
            { place: 3, row: null },
          ]
        : top3.length === 2
          ? [
              { place: 2, row: top3[1] ?? null },
              { place: 1, row: top3[0] ?? null },
              { place: 3, row: null },
            ]
          : [
              { place: 2, row: top3[1] ?? null },
              { place: 1, row: top3[0] ?? null },
              { place: 3, row: top3[2] ?? null },
            ]

  return (
    <div className="flex flex-col gap-4">
      <Card
        size="default"
        tone="surface"
        className="border border-brand-border px-2 pt-4 pb-3 sm:px-4"
      >
        <div className="flex items-end justify-center gap-1.5 sm:gap-3">
          {slots.map(({ place, row }) => (
            <div
              key={place}
              className="flex min-h-0 min-w-0 flex-1 flex-col items-center gap-2"
            >
              <div className="flex min-h-[5.75rem] w-full flex-col items-center justify-end gap-1.5">
                {row ? (
                  <>
                    <Avatar
                      photoUrl={row.photoUrl}
                      initial={initialsFromDisplayName(row.displayName)}
                      size="md"
                    />
                    <p
                      className="max-w-full truncate px-0.5 text-center text-xs font-semibold text-brand-text"
                      title={row.displayName}
                    >
                      {row.displayName}
                    </p>
                    <div className="flex items-center gap-1 rounded-full bg-brand-bg-soft px-2 py-0.5 text-[11px] tabular-nums">
                      <span className="font-semibold text-brand-primary-strong">
                        {row.avgBand}
                      </span>
                      <span className="text-brand-text-muted">·</span>
                      <span className="text-brand-text-muted">
                        {row.sessionsScored}
                      </span>
                    </div>
                  </>
                ) : null}
              </div>
              <div
                className={`mt-auto flex w-full max-w-[6.5rem] flex-col items-center justify-end rounded-t-xl border border-brand-border bg-gradient-to-b from-brand-surface-2 to-brand-bg-soft ${podiumPedestalHeight(place)}`}
              >
                {row ? (
                  <span
                    className={`pb-1 ${podiumPedestalRankClass(place)}`}
                    title={`${t(locale, 'leaderboard.rank')} ${row.rank}`}
                  >
                    {row.rank}
                  </span>
                ) : (
                  <span
                    className="pb-1 text-2xl font-bold tabular-nums text-brand-text-subtle/20"
                    aria-hidden
                  >
                    {place}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {rest.length > 0 ? (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-text-muted px-0.5">
            {t(locale, 'leaderboard.restHeading')}
          </p>
          <Card
            size="default"
            tone="surface"
            className="flex flex-col divide-y divide-brand-list-divider border border-brand-border overflow-hidden p-0"
          >
            {rest.map((row) => {
              const isYou = userId === row.userId
              return (
                <div
                  key={row.userId}
                  className={`flex items-center gap-3 px-3 py-2.5 ${
                    isYou
                      ? 'bg-brand-primary-soft/45 ring-2 ring-inset ring-brand-primary/30'
                      : ''
                  }`}
                >
                  <span className="w-6 shrink-0 text-center text-sm font-medium tabular-nums text-brand-text-muted">
                    {row.rank}
                  </span>
                  <Avatar
                    photoUrl={row.photoUrl}
                    initial={initialsFromDisplayName(row.displayName)}
                    size="sm"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-brand-text">
                      {row.displayName}
                    </p>
                    <p className="text-[11px] text-brand-text-muted tabular-nums">
                      {row.sessionsScored} {t(locale, 'leaderboard.sessions')}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <span className="text-base font-semibold tabular-nums text-brand-primary-strong">
                      {row.avgBand}
                    </span>
                  </div>
                </div>
              )
            })}
          </Card>
        </div>
      ) : null}
    </div>
  )
}
